import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';

import DeleteModal from '@atom/components/common/DeleteModal';
import folderMove from '@atom/components/common/svgIcons/folderMove.svg';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import {
  DELETE_ANALYTICS_CARD,
  MOVE_ANALYTICS_CARDS,
  UPDATE_ANALYTICS_CARD,
} from '@atom/graph/analytics';
import { Icon, Menu, Snackbar } from '@atom/mui';
import { DeleteIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  AnalyticsCard,
  AnalyticsCardUpdateInput,
  AnalyticsFolderTree,
} from '@atom/types/analytics';

import EditAnalyticsCardModal from './analyticsModals/EditAnalyticsCardModal';
import MoveAnalyticsCardModal from './analyticsModals/MoveAnalyticsCardModal';
import AnalyticsContext from './AnalyticsContext';

import classes from './analyticsFolderTree.module.css';

const { MenuItem } = Menu;

interface Props {
  card: AnalyticsCard;
  refetch: () => void;
}

const CardOptions = ({ card, refetch }: Props) => {
  const { setBulkMoveSelected } = useContext(AnalyticsContext);

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [moveModalOpen, setMoveModalOpen] = useState<boolean>(false);

  const [updateCard] = useMutation(UPDATE_ANALYTICS_CARD);

  const handleUpdateCard = async (input: AnalyticsCardUpdateInput) => {
    try {
      await updateCard({
        variables: {
          input,
        },
      });
      Snackbar.info({
        message: 'Updated.',
      });
      refetch();
      setBulkMoveSelected(new Set([]));
    } catch (error) {
      Snackbar.error({
        message: `An error occurred while updating "${card?.name}". Please Try Again.`,
      });
    } finally {
      setEditModalOpen(false);
    }
  };

  const [deleteCard] = useMutation(DELETE_ANALYTICS_CARD);

  const handleDeleteCard = async () => {
    try {
      await deleteCard({ variables: { id: card.id } });
      Snackbar.info({
        message: 'Successfully deleted card.',
      });
      refetch();
      setBulkMoveSelected(new Set([]));
    } catch (error) {
      Snackbar.error({
        message: `An error occurred". Please Try Again.`,
      });
    }
  };

  const [moveCard] = useMutation(MOVE_ANALYTICS_CARDS);

  const handleMoveCard = async (folder: AnalyticsFolderTree) => {
    try {
      await moveCard({
        variables: {
          input: { destFolderId: folder.id, analyticsIds: [card.id] },
        },
      });
      Snackbar.info({
        message: `Moved "${card.name}" to folder "${folder.name}"`,
      });
      refetch();
      setBulkMoveSelected(new Set([]));
      setMoveModalOpen(false);
    } catch (error) {
      Snackbar.error({
        message: `An error occurred". Please Try Again.`,
      });
    }
  };

  return (
    <div
      className={classes.optionsWrapper}
      data-cy={`analyticsCardMenu-${card?.name}`}
    >
      <Menu IconProps={{ color: colors.neutral.gray }}>
        <MenuItem
          className={classes.menuItem}
          onClick={() => setMoveModalOpen(true)}
          data-cy={`analyticsMoveCardOption-${card?.name}`}
        >
          <div className={classes.primaryTextBox}>
            <div className={classes.iconBox}>
              <img src={folderMove} />
              <div className={classes.primaryText}>Move to Folder</div>
            </div>
          </div>
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={() => setEditModalOpen(true)}
          data-cy={`analyticsEditCardOption-${card?.name}`}
        >
          <div className={classes.primaryTextBox}>
            <div className={classes.iconBox}>
              <img src={renameIcon} />
              <div className={classes.primaryText}>Edit</div>
            </div>
          </div>
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={() => setDeleteModalOpen(true)}
          data-cy={`analyticsDeleteCardOption-${card?.name}`}
        >
          <div className={classes.primaryTextBox}>
            <div className={classes.iconBox}>
              <Icon color={colors.neutral.gray} icon={DeleteIcon} />
              <div className={classes.primaryText}>Delete</div>
            </div>
          </div>
        </MenuItem>
      </Menu>
      <MoveAnalyticsCardModal
        open={moveModalOpen}
        onClose={() => setMoveModalOpen(false)}
        handleMoveCard={handleMoveCard}
        title="Move to Folder"
      />
      <EditAnalyticsCardModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        handleUpdateCard={handleUpdateCard}
        card={card}
      />
      <DeleteModal
        open={deleteModalOpen}
        title="Delete Card"
        content="Are you sure you want to delete this card?"
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteCard}
      />
    </div>
  );
};

export default CardOptions;
